import { useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, useApiUrl, useTranslate } from "@refinedev/core";
import { Button, ColorField, Field, Modal, SearchIcon, SelectField, type SelectOption } from "@fawkes/ui";

import { getAccessToken } from "../../auth";
import "./DierenStamgegevens.css";

// jve-settings-dieren: the React port of the Jmix `SettingsDashboardView.buildAnimalsManagementSection`
// (Organisatie › Dieren tile). A diersoort selector scopes the three name-only cards (Vachten/Kleuren/
// Rassen); Kenneleigenschappen is organisation-wide and carries a colour. Each card has a searchable,
// scrollable list with add/edit and a delete dialog that surfaces linked-animal count + relink (the
// critical link-aware delete ported from `AnimalSettingsMaintenanceService`). Backed by the hexagonal
// `/api/animal-settings` resource. Opened from the settings dashboard; `onBack` returns to the tiles.

interface AnimalRow {
  id: number;
  naam: string;
  color: string | null;
}
interface Species {
  id: number;
  naam: string;
}

interface CardDef {
  type: string;
  titleKey: string;
  subtitleKey: string;
  addKey: string;
  searchKey: string;
  hasColor: boolean;
  speciesScoped: boolean;
}

// The four reference-data types, in the Jmix card order. `kenneleigenschap` carries a colour and is
// not species-scoped; the rest are name-only and filtered by the selected diersoort.
const CARDS: CardDef[] = [
  {
    type: "vacht",
    titleKey: "instellingen.dieren.vacht.title",
    subtitleKey: "instellingen.dieren.vacht.subtitle",
    addKey: "instellingen.dieren.vacht.add",
    searchKey: "instellingen.dieren.vacht.search",
    hasColor: false,
    speciesScoped: true,
  },
  {
    type: "kleur",
    titleKey: "instellingen.dieren.kleur.title",
    subtitleKey: "instellingen.dieren.kleur.subtitle",
    addKey: "instellingen.dieren.kleur.add",
    searchKey: "instellingen.dieren.kleur.search",
    hasColor: false,
    speciesScoped: true,
  },
  {
    type: "kenneleigenschap",
    titleKey: "instellingen.dieren.kenneleigenschap.title",
    subtitleKey: "instellingen.dieren.kenneleigenschap.subtitle",
    addKey: "instellingen.dieren.kenneleigenschap.add",
    searchKey: "instellingen.dieren.kenneleigenschap.search",
    hasColor: true,
    speciesScoped: false,
  },
  {
    type: "ras",
    titleKey: "instellingen.dieren.ras.title",
    subtitleKey: "instellingen.dieren.ras.subtitle",
    addKey: "instellingen.dieren.ras.add",
    searchKey: "instellingen.dieren.ras.search",
    hasColor: false,
    speciesScoped: true,
  },
];

const DEFAULT_COLOR = "#5a7d6c"; // parity with the Jmix hub default

interface EditState {
  card: CardDef;
  id: number | null;
  naam: string;
  color: string;
}
interface DeleteState {
  card: CardDef;
  row: AnimalRow;
  usage: number;
  reassignTo: string;
  options: AnimalRow[];
}

export function DierenStamgegevens({ onBack }: { onBack: () => void }) {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const { mutate: mutateCustom, isLoading: isSaving } = useCustomMutation();

  const { data: speciesData } = useCustom<Species[]>({ url: "animal-settings/species", method: "get" });
  const species = useMemo(() => speciesData?.data ?? [], [speciesData]);

  const [selectedSpecies, setSelectedSpecies] = useState("");
  const [version, setVersion] = useState(0);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [del, setDel] = useState<DeleteState | null>(null);
  const refresh = () => setVersion((v) => v + 1);

  // Default the selector to "hond" (dog) when present, else the first species — mirrors the Jmix view.
  useEffect(() => {
    if (selectedSpecies !== "" || species.length === 0) return;
    const hond = species.find((s) => s.naam.toLowerCase().includes("hond"));
    setSelectedSpecies(String((hond ?? species[0]).id));
  }, [species, selectedSpecies]);

  function startCreate(card: CardDef) {
    setEdit({ card, id: null, naam: "", color: DEFAULT_COLOR });
  }
  function startEdit(card: CardDef, row: AnimalRow) {
    setEdit({ card, id: row.id, naam: row.naam, color: row.color ?? DEFAULT_COLOR });
  }

  function save() {
    if (!edit) return;
    const { card } = edit;
    const values: Record<string, unknown> = { naam: edit.naam.trim() };
    if (card.hasColor) values.color = edit.color;
    if (card.speciesScoped && edit.id == null) values.species = Number(selectedSpecies);
    // Relative path: the dataProvider's `custom` prepends the /api base (see dataProvider.ts).
    const base = `animal-settings/${card.type}`;
    mutateCustom(
      { url: edit.id == null ? base : `${base}/${edit.id}`, method: edit.id == null ? "post" : "put", values },
      { onSuccess: () => { setEdit(null); refresh(); } },
    );
  }

  // From a row's delete button: resolve linked-animal count, then delete straight away or open the dialog.
  async function askDelete(card: CardDef, row: AnimalRow, rows: AnimalRow[]) {
    let usage = 0;
    try {
      // Raw fetch (not via the dataProvider), so it DOES need the apiUrl base prepended.
      const res = await fetch(`${apiUrl}/animal-settings/${card.type}/${row.id}/usage`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${getAccessToken() ?? ""}` },
      });
      if (res.ok) usage = (await res.json()).count ?? 0;
    } catch {
      usage = 0;
    }
    if (usage === 0) {
      doDelete(card, row.id, null);
    } else {
      setDel({ card, row, usage, reassignTo: "", options: rows.filter((o) => o.id !== row.id) });
    }
  }

  function doDelete(card: CardDef, id: number, reassignTo: string | null) {
    const qs = reassignTo ? `?reassignTo=${reassignTo}` : "";
    mutateCustom(
      { url: `animal-settings/${card.type}/${id}${qs}`, method: "delete", values: {} },
      { onSuccess: () => { setDel(null); refresh(); } },
    );
  }

  const reassignOptions: SelectOption[] = del
    ? [
        { value: "", label: t("instellingen.dieren.delete.choosePlaceholder") },
        ...del.options.map((o) => ({ value: String(o.id), label: o.naam })),
      ]
    : [];

  return (
    <div className="settings-animals-section">
      <div className="settings-animals-head">
        <div>
          <h2 className="settings-animals-title">{t("instellingen.dieren.title")}</h2>
          <p className="settings-animals-subtitle">{t("instellingen.dieren.subtitle")}</p>
        </div>
        <Button variant="secondary" onClick={onBack}>{t("common.back")}</Button>
      </div>

      <div className="settings-animals-filter-row">
        <span className="settings-animals-filter-label">{t("instellingen.dieren.speciesLabel")}</span>
        <SelectField
          label=""
          className="settings-animals-filter-selector"
          aria-label={t("instellingen.dieren.speciesLabel")}
          value={selectedSpecies}
          onChange={(e) => setSelectedSpecies(e.target.value)}
          options={species.map((s) => ({ value: String(s.id), label: s.naam }))}
        />
      </div>

      <div className="settings-animals-grid">
        {CARDS.map((card) => (
          <AnimalCard
            key={card.type}
            card={card}
            speciesId={selectedSpecies}
            version={version}
            onAdd={() => startCreate(card)}
            onEdit={(row) => startEdit(card, row)}
            onDelete={(row, rows) => askDelete(card, row, rows)}
          />
        ))}
      </div>

      {/* Add / edit modal */}
      <Modal
        open={edit != null}
        title={edit ? (edit.id == null ? t("instellingen.dieren.create") : t("instellingen.dieren.edit")) : ""}
        onClose={() => setEdit(null)}
        footer={
          edit && (
            <>
              <Button variant="secondary" onClick={() => setEdit(null)} disabled={isSaving}>{t("common.cancel")}</Button>
              <Button variant="primary" onClick={save} disabled={isSaving || edit.naam.trim() === ""}>{t("common.save")}</Button>
            </>
          )
        }
      >
        {edit && (
          <>
            <Field
              label={t("instellingen.dieren.naam")}
              value={edit.naam}
              onChange={(e) => setEdit({ ...edit, naam: e.target.value })}
              required
            />
            {edit.card.hasColor && (
              <ColorField
                label={t("instellingen.dieren.kleurLabel")}
                value={edit.color}
                onChange={(e) => setEdit({ ...edit, color: e.target.value })}
              />
            )}
          </>
        )}
      </Modal>

      {/* Delete-with-relink modal */}
      <Modal
        open={del != null}
        title={t("instellingen.dieren.delete.title")}
        onClose={() => setDel(null)}
        footer={
          del && (
            <>
              <Button variant="secondary" onClick={() => setDel(null)} disabled={isSaving}>{t("common.cancel")}</Button>
              <Button variant="secondary" onClick={() => doDelete(del.card, del.row.id, null)} disabled={isSaving}>
                {t("instellingen.dieren.delete.removeLinks")}
              </Button>
              <Button
                variant="primary"
                disabled={isSaving || del.reassignTo === ""}
                onClick={() => doDelete(del.card, del.row.id, del.reassignTo)}
              >
                {t("instellingen.dieren.delete.transfer")}
              </Button>
            </>
          )
        }
      >
        {del && (
          <div data-testid="delete-relink-dialog">
            <p className="settings-animals-delete-info">
              {t("instellingen.dieren.delete.inUse", { args: [del.usage, del.row.naam] })}
            </p>
            <SelectField
              label={t("instellingen.dieren.delete.transferTo")}
              options={reassignOptions}
              value={del.reassignTo}
              onChange={(e) => setDel({ ...del, reassignTo: e.target.value })}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

interface AnimalCardProps {
  card: CardDef;
  speciesId: string;
  version: number;
  onAdd: () => void;
  onEdit: (row: AnimalRow) => void;
  onDelete: (row: AnimalRow, rows: AnimalRow[]) => void;
}

function AnimalCard({ card, speciesId, version, onAdd, onEdit, onDelete }: AnimalCardProps) {
  const t = useTranslate();
  const [search, setSearch] = useState("");

  const url = card.speciesScoped ? `animal-settings/${card.type}?species=${speciesId}` : `animal-settings/${card.type}`;
  const { data, isLoading, isError } = useCustom<AnimalRow[]>({
    url,
    method: "get",
    queryOptions: {
      queryKey: ["animal-settings", card.type, card.speciesScoped ? speciesId : "all", version],
      enabled: !card.speciesScoped || speciesId !== "",
    },
  });
  const rows = data?.data ?? [];
  const needle = search.trim().toLowerCase();
  const filtered = needle === "" ? rows : rows.filter((r) => r.naam.toLowerCase().includes(needle));

  return (
    <section className="settings-animals-card">
      <header className="settings-animals-card-header">
        <h3 className="settings-animals-card-title">{t(card.titleKey)}</h3>
      </header>
      <p className="settings-animals-card-subtitle">{t(card.subtitleKey)}</p>

      <div className="settings-animals-search">
        <SearchIcon className="settings-animals-search-icon" width={14} height={14} />
        <input
          className="settings-animals-search-input"
          placeholder={t(card.searchKey)}
          aria-label={t(card.searchKey)}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className={`settings-animals-list${card.hasColor ? " settings-animals-list-with-color" : ""}`}>
        {isLoading ? (
          <span className="settings-animals-empty">{t("common.loading")}</span>
        ) : isError ? (
          <span className="settings-animals-empty">{t("instellingen.dieren.loadFailed")}</span>
        ) : filtered.length === 0 ? (
          <span className="settings-animals-empty">
            {needle === "" ? t("instellingen.dieren.empty") : t("instellingen.dieren.noResults")}
          </span>
        ) : (
          filtered.map((row) => (
            <div
              key={row.id}
              className={`settings-animals-row${card.hasColor ? " settings-animals-row-with-color" : ""}`}
            >
              <span className="settings-animals-row-name">{row.naam}</span>
              {card.hasColor && (
                <span className="settings-animals-color-chip" style={{ backgroundColor: row.color ?? DEFAULT_COLOR }}>
                  {row.naam}
                </span>
              )}
              <div className="settings-animals-row-actions">
                <button
                  type="button"
                  className="settings-animals-row-action"
                  aria-label={`${t("instellingen.dieren.editRow")}: ${row.naam}`}
                  title={t("instellingen.dieren.editRow")}
                  onClick={() => onEdit(row)}
                >
                  ✎
                </button>
                <button
                  type="button"
                  className="settings-animals-row-action settings-animals-row-action-delete"
                  aria-label={`${t("instellingen.dieren.deleteRow")}: ${row.naam}`}
                  title={t("instellingen.dieren.deleteRow")}
                  onClick={() => onDelete(row, rows)}
                >
                  ✕
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <Button variant="primary" className="settings-animals-add-button" onClick={onAdd}>
        {t(card.addKey)}
      </Button>
    </section>
  );
}
